import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Upload, Trash2, ImageIcon, Grid3X3, List, X, Loader2, SlidersHorizontal, Images } from 'lucide-react';
import {
  getGalleryImages, uploadGalleryImages, deleteGalleryImage,
  getHeroSliderImages, uploadHeroSliderImages, deleteHeroSliderImage,
} from '../services/adminService';

// ── Reusable image section ────────────────────────────────────────────────────
const ImageSection = ({ title, description, icon: Icon, accentColor, fetchFn, uploadFn, deleteFn }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFn();
      setImages(data);
    } catch {
      toast({ title: 'Error', description: `Failed to load ${title.toLowerCase()} images.`, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [fetchFn, title, toast]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    setSelectedFiles(files);
    setPreviews(files.map(f => ({ name: f.name, url: URL.createObjectURL(f) })));
  };

  const clearSelection = () => {
    previews.forEach(p => URL.revokeObjectURL(p.url));
    setSelectedFiles([]);
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    try {
      await uploadFn(selectedFiles);
      toast({ title: 'Uploaded', description: `${selectedFiles.length} image(s) added to ${title.toLowerCase()}.` });
      clearSelection();
      fetchImages();
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteFn(id);
      setImages(prev => prev.filter(img => img.id !== id));
      toast({ title: 'Deleted', description: 'Image removed.' });
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${accentColor.bg}`}>
              <Icon className={`h-5 w-5 ${accentColor.icon}`} />
            </div>
            <div>
              <CardTitle className="text-white">{title}</CardTitle>
              <CardDescription className="text-slate-400 mt-0.5">{description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${accentColor.badge}`}>
              {images.length} image{images.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'} size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? `${accentColor.btn}` : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'} size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? `${accentColor.btn}` : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Drop zone */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${accentColor.dropzone}`}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
          <p className="text-slate-300 font-medium">Click to select images</p>
          <p className="text-slate-500 text-sm mt-1">JPG, PNG, GIF — auto-converted to WebP</p>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
        </div>

        {/* Selected previews */}
        {previews.length > 0 && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3">
              {previews.map((p, i) => (
                <div key={i} className="relative group">
                  <img src={p.url} alt={p.name} className="h-20 w-20 object-cover rounded-lg border border-slate-600" />
                  <button
                    onClick={() => {
                      URL.revokeObjectURL(p.url);
                      setSelectedFiles(selectedFiles.filter((_, idx) => idx !== i));
                      setPreviews(previews.filter((_, idx) => idx !== i));
                    }}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={uploading} className={`${accentColor.btn}`}>
                {uploading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                  : <><Upload className="h-4 w-4 mr-2" />Upload {selectedFiles.length} image{selectedFiles.length > 1 ? 's' : ''}</>}
              </Button>
              <Button variant="outline" onClick={clearSelection} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Images grid / list */}
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className={`h-8 w-8 animate-spin ${accentColor.icon}`} />
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-14 border-2 border-dashed border-slate-700 rounded-xl">
            <ImageIcon className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 font-medium">No images yet</p>
            <p className="text-slate-500 text-sm mt-1">Upload images using the panel above.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {images.map((img) => (
              <div key={img.id} className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-800/50 aspect-video">
                <img src={img.url} alt={img.fileName} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button size="sm" variant="outline" disabled={deletingId === img.id} onClick={() => handleDelete(img.id)}
                    className="border-red-500/60 text-red-400 hover:bg-red-500/20">
                    {deletingId === img.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs truncate">{img.fileName}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {images.map((img) => (
              <div key={img.id} className="flex items-center gap-4 p-3 bg-slate-700/40 rounded-xl border border-slate-600/50">
                <img src={img.url} alt={img.fileName} className="w-16 h-10 object-cover rounded-lg flex-shrink-0" />
                <p className="text-slate-300 text-sm flex-1 truncate">{img.fileName}</p>
                <Button size="sm" variant="outline" disabled={deletingId === img.id} onClick={() => handleDelete(img.id)}
                  className="border-red-600/50 text-red-400 hover:bg-red-600/20 shrink-0">
                  {deletingId === img.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ── Accent colour themes per section ─────────────────────────────────────────
const sliderAccent = {
  bg: 'bg-amber-500/20',
  icon: 'text-amber-400',
  badge: 'bg-amber-500/15 text-amber-300',
  btn: 'bg-amber-600 hover:bg-amber-700 text-white',
  dropzone: 'border-slate-600 hover:border-amber-500 hover:bg-amber-500/5',
};

const galleryAccent = {
  bg: 'bg-blue-500/20',
  icon: 'text-blue-400',
  badge: 'bg-blue-500/15 text-blue-300',
  btn: 'bg-blue-600 hover:bg-blue-700 text-white',
  dropzone: 'border-slate-600 hover:border-blue-500 hover:bg-blue-500/5',
};

// ── Gallery page ──────────────────────────────────────────────────────────────
const Gallery = () => (
  <div className="space-y-8">
    {/* Header */}
    <div>
      <h1 className="text-3xl font-bold text-white flex items-center gap-2">
        <Images className="h-7 w-7 text-blue-400" />
        Media Library
      </h1>
      <p className="text-slate-400 mt-1">Manage your hotel's images — hero slider and gallery</p>
    </div>

    {/* Hero Slider section */}
    <ImageSection
      title="Hero Slider"
      description="Full-screen images that cycle on the home page — use wide landscape photos (16:9)"
      icon={SlidersHorizontal}
      accentColor={sliderAccent}
      fetchFn={getHeroSliderImages}
      uploadFn={uploadHeroSliderImages}
      deleteFn={deleteHeroSliderImage}
    />

    {/* Gallery section */}
    <ImageSection
      title="Gallery"
      description="Photos shown on the Gallery page — any orientation works"
      icon={ImageIcon}
      accentColor={galleryAccent}
      fetchFn={getGalleryImages}
      uploadFn={uploadGalleryImages}
      deleteFn={deleteGalleryImage}
    />
  </div>
);

export default Gallery;
